import React, { useCallback, useContext, useEffect } from 'react';
import { EDTextEdit } from '../../../../BaseComponents/TextEdit/EDTextEdit';
import {
  AvlidenFromBisattningQuery,
  AvlidenFromBisattningQueryVariables,
} from '../Queries/_QueryTypes/AvlidenFromBisattningQuery';
import avlidenFromBisattningQuery from '../Queries/AvlidenFromBisattningQuery.gql';
import { useQuery } from '@apollo/client';
import { ActiveDatabaseContext } from '../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { IBusinessDataFormContext } from '../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import {
  TEDBOBisattningshandelseTypEnum,
  TEDOPOrgPersLokLOKALADREnum,
  TEDOPOrgPersLokTypEnum,
} from '../../../../globalQueryTypes';
import { ActiveRoutineContext } from '../../../Routing/ActiveRoutine/ActiveRoutineContext';
import { TEDParsePersNr } from '../../../../Utils/Formatters/Parsers/TEDParsePersNr';
import { ParseResult } from '../../../../Utils/Formatters/FormatterTypes';

interface IPlaceringInfo {
  rumBen: string | undefined;
  delBen: string | undefined;
  placeringID: string | undefined;
  placeringBen: string | undefined;
}
interface IProps {
  formContext: IBusinessDataFormContext<any, unknown>;
  aktivBisattningstyp?: string;
  persNr: string;
  setPersNr: React.Dispatch<React.SetStateAction<string>>;
  fetchPacemaker: boolean;
  onCompleteLamnaIn?: (
    avlidenID: string | undefined,
    avlidenVersion: number | undefined,
    begravningDatumTid: string,
    gravsattningDatumTid: string,
    avlidenPersonID: string | undefined,
    avlidenPersonVersion: number | undefined,
    dodsDatum: string | undefined,
    avlidenPersonAdressID: string | undefined,
    avlidenPersonAdressVersion: number | undefined,
    bisattningsID: string | undefined,
    bisattningVersion: number | undefined,
    begravningsbyraID: string | undefined,
    bisattningBegravningsbyraID: string | undefined,
    bisattningTransportorID: string | undefined,
    senasteBisattningsTyp: TEDBOBisattningshandelseTypEnum | undefined,
    noteringar: string | undefined,
    bisattningsTypID: string | undefined,
    bisattningsTyp: string | undefined,
    aktNummer: string | null | undefined
  ) => void;
  onCompleteHamtaUt?: (
    avlidenID: string | undefined,
    avlidenVersion: number | undefined,
    gravsattningDatumTid: string,
    avlidenPersonID: string | undefined,
    avlidenPersonVersion: number | undefined,
    avlidenPersonAdressID: string | undefined,
    avlidenPersonAdressVersion: number | undefined,
    bisattningsID: string | undefined,
    bisattningVersion: number | undefined,
    begravningsbyraID: string | undefined,
    bisattningBegravningsbyraID: string | undefined,
    bisattningTransportorID: string | undefined,
    senasteBisattningsTyp: TEDBOBisattningshandelseTypEnum | undefined,
    bisattningsTypID: string | undefined,
    bisattningsTyp: string | undefined,
    placering: string | undefined | null,
    aktNummer: string | null | undefined,
    typUrna: string | null | undefined,
    gravsplats: string | undefined,
    gravsattningsort: string | undefined,
    tvistKrem: string | null | undefined,
    tvistGravsattning: string | null | undefined
  ) => void;
  onCompleteNyPlacering?: (
    avlidenID: string | undefined,
    bisattningsID: string | undefined,
    bisattningVersion: number | undefined,
    bisattningsTyp: string | undefined,
    senasteBisattningsTyp: TEDBOBisattningshandelseTypEnum | undefined,
    placering: string | undefined | null,
    placeringInfo: IPlaceringInfo,
    room: string | undefined
  ) => void;
}

const EDOPAvlidenSearchButtons: React.FC<IProps> = props => {
  const { persNr, setPersNr } = props;
  const { activeDatabase } = useContext(ActiveDatabaseContext);
  const activeRoutine = useContext(ActiveRoutineContext);

  const { data: avlidenData } = useQuery<
    AvlidenFromBisattningQuery,
    AvlidenFromBisattningQueryVariables
  >(avlidenFromBisattningQuery, {
    variables: {
      persNr,
      dbname: activeDatabase || '',
      routine: activeRoutine?.rutinInfo.rutinID,
    },
    skip: persNr.length < 12,
    fetchPolicy: 'network-only',
    onCompleted: () => {
      const onlyNumbers = persNr.replaceAll('-', '');
      if (onlyNumbers.length === 12) {
        const avliden = avlidenData?.db?.OPAvliden_All?.[0];
        const avlidenOP = avliden?._Associations?.Person;
        const avlidenOPAdress = avlidenOP?._Associations?.Adress;
        const bisattning = avliden?._Associations?.Bisattning;
        const gravsattningOrt = avliden?._Associations?.Gravsattningsort;
        const tvistKrem = avliden?._Associations?.Bisattning?.TvistKrem;
        const tvistGravsattning =
          avliden?._Associations?.Bisattning?.TvistGravs;

        /**
         * Om den avlidne har en bisättning med en begravningsbyrå hämtar vi den enligt stegen nedan:
         * 1. Lokal begravningsbyrå ---> retunerar OriginalOPL.id (om det finns, annars null)
         * 2. Extern begravningsbyrå --> retunerar id för den byrån
         *
         * Om den avlidne saknar bisättning med begravningsbyrå hämtar vi från begravning
         * 1. retunerar OriginalOPL.id (om det finns, annars null)
         */
        const begrbyraID = avlidenData?.db?.OPAvliden_All?.[0]?._Associations
          ?.Bisattning?._Associations?.Begravningsbyra?.id
          ? avlidenData?.db?.OPAvliden_All?.[0]?._Associations?.Bisattning
              ?._Associations?.Begravningsbyra?.LOKALADR ===
            TEDOPOrgPersLokLOKALADREnum.LOKAL
            ? avlidenData?.db?.OPAvliden_All?.[0]?._Associations?.Bisattning
                ?._Associations?.Begravningsbyra?._Associations?.OriginalOPL
                ?.id ?? undefined
            : avlidenData?.db?.OPAvliden_All?.[0]?._Associations?.Bisattning
                ?._Associations?.Begravningsbyra.id
          : avlidenData?.db?.OPAvliden_All?.[0]?._Associations?.Begravning
              ?._Associations?.Begrbyra?._Associations?.OriginalOPL?.id ??
            undefined;
        if (avliden) {
          let commonValues = {};
          if (props?.aktivBisattningstyp) {
            if (props.aktivBisattningstyp === 'Urna / Aska') {
              commonValues = {
                TYP_URNA: bisattning?.TYP_URNA,
              };
            } else if (props.aktivBisattningstyp === 'Kista') {
              commonValues = {
                KISTMATT: bisattning?.KISTMATT,
                TYP_URNA: bisattning?.TYP_URNA,
                EXTRA_STOR_KISTA: bisattning?.EXTRA_STOR_KISTA,
                TYP_KISTA: bisattning?.TYP_KISTA,
                ...(props.fetchPacemaker && {
                  PACEMAKER: bisattning?.PACEMAKER,
                }),
              };
            }
          }

          if (
            !props?.aktivBisattningstyp ||
            props.aktivBisattningstyp !== 'Tom Urna'
          ) {
            const avlidnaValues = {
              id: avliden?.id,
              _version: avliden?._version,
              KREMERING: avliden?.KREMERING ?? 'OBESTAMD',
              BEGRAVNINGSPLATS: avliden?.BEGRAVNINGSPLATS,
              GRAVSPLATS: avliden?.GRAVSPLATS,
              AKT_MED_URNA: avliden?.AKT_MED_URNA,
              AVLIDEN_DATUM: avliden?.AVLIDEN_DATUM,
            };

            const personValues = {
              id: avlidenOP?.id,
              _version: avlidenOP?._version,
              _typ: TEDOPOrgPersLokTypEnum.PERSON,
              PERS_NR: avlidenOP?.PERS_NR ?? persNr.replaceAll('-', ''),
              FORNAMN: avlidenOP?.FORNAMN ?? '',
              EFTERNAMN: avlidenOP?.EFTERNAMN ?? '',
              _Associations: { Adress_Values: {} },
            };

            const gravsattningsortValues = {
              id: gravsattningOrt?.id,
              _version: gravsattningOrt?._version,
              ORG_NAMN: gravsattningOrt?.ORG_NAMN,
            };

            props.formContext?.update([
              [
                {
                  operation: 'OwnFields',
                  values: commonValues,
                },
                {
                  operation: 'Values',
                  associationName: 'Avlidna',
                  values: avlidnaValues,
                },
                {
                  operation: 'Values',
                  associationName: 'Person',
                  values: personValues,
                },
              ],
              [
                { operation: 'Values', associationName: 'Avlidna' },
                {
                  operation: 'Values',
                  associationName: 'Gravsattningsort',
                  values: gravsattningsortValues,
                },
              ],
            ]);
          } else
            props.formContext?.update([
              {
                operation: 'Values',
                associationName: 'Avlidna',
                values: {
                  id: avliden?.id,
                  _version: avliden?._version,
                },
              },
              {
                operation: 'Values',
                associationName: 'Person',
                values: {
                  id: avlidenOP?.id,
                  _version: avlidenOP?._version,
                  _typ: TEDOPOrgPersLokTypEnum.PERSON,
                  PERS_NR: avlidenOP?.PERS_NR ?? persNr.replaceAll('-', ''),
                  FORNAMN: avlidenOP?.FORNAMN ?? '',
                  EFTERNAMN: avlidenOP?.EFTERNAMN ?? '',
                  _Associations: { Adress_Values: {} },
                },
              },
            ]);
          if (begrbyraID)
            props.formContext?.update([
              {
                operation: 'Ref',
                associationName: 'Begravningsbyra',
                id: begrbyraID,
                values: {
                  KATEGORI: 'BEGRAVNINGSBYRA',
                  LOKALADR: 'EXTERN',
                },
              },
            ]);
          const bisBegrbyraID =
            avlidenData?.db?.OPAvliden_All?.[0]?._Associations?.Bisattning
              ?._Associations?.Begravningsbyra?.id;
          const bisTransportorID =
            avlidenData?.db?.OPAvliden_All?.[0]?._Associations?.Bisattning
              ?._Associations?.Transportor?.id;
          const dodsDatum = avliden?.AVLIDEN_DATUM ?? '';
          if (props.onCompleteLamnaIn) {
            props.onCompleteLamnaIn(
              avliden?.id,
              avliden?._version,
              avliden?.BEGR_DATUM_TID ?? '',
              avliden?.GRAVS_DATUM_TID ?? '',
              avlidenOP?.id,
              avlidenOP?._version,
              dodsDatum,
              avlidenOPAdress?.id,
              avlidenOPAdress?._version,
              bisattning?.id,
              bisattning?._version,
              begrbyraID,
              bisBegrbyraID,
              bisTransportorID,
              bisattning?._Associations?.SenasteHandelse2?._typ,
              bisattning?.NOTERINGAR ?? '',
              bisattning?._Associations?.SenasteHandelse2?._Associations
                ?.Bisattningstyp?.id,
              bisattning?._Associations?.SenasteHandelse2?._Associations
                ?.Bisattningstyp?.BENAMNING,
              bisattning?.AKTNUMMER
            );
          } else if (props.onCompleteHamtaUt) {
            props.onCompleteHamtaUt(
              avliden?.id,
              avliden?._version,
              avliden?.GRAVS_DATUM_TID ?? '',
              avlidenOP?.id,
              avlidenOP?._version,
              avlidenOPAdress?.id,
              avlidenOPAdress?._version,
              bisattning?.id,
              bisattning?._version,
              begrbyraID,
              bisBegrbyraID,
              bisTransportorID,
              bisattning?._Associations?.SenasteHandelse2?._typ,
              bisattning?._Associations?.SenasteHandelse2?._Associations
                ?.Bisattningstyp?.id,
              bisattning?._Associations?.SenasteHandelse2?._Associations
                ?.Bisattningstyp?.BENAMNING,
              bisattning?._Associations?.SenasteHandelse2?.PLACERING_EJ_MALL,
              bisattning?.AKTNUMMER,
              bisattning?.TYP_URNA,
              avliden?.GRAVSPLATS || undefined,
              gravsattningOrt?.ORG_NAMN || undefined,
              tvistKrem,
              tvistGravsattning
            );
          } else if (props.onCompleteNyPlacering) {
            props.onCompleteNyPlacering(
              avliden?.id,
              bisattning?.id,
              bisattning?._version,
              bisattning?._Associations?.SenasteHandelse2?._Associations
                ?.Bisattningstyp?.BENAMNING,
              bisattning?._Associations?.SenasteHandelse2?._typ,
              bisattning?._Associations?.SenasteHandelse2?.PLACERING_EJ_MALL,
              {
                delBen: undefined,
                placeringBen:
                  bisattning?._Associations?.SenasteHandelse2?._Associations
                    ?.Placering?.BENAMNING || undefined,
                placeringID:
                  bisattning?._Associations?.SenasteHandelse2?._Associations
                    ?.Placering?.id,
                rumBen: undefined,
              },
              bisattning?._Associations?.SenasteHandelse2?._Associations
                ?.Placering?._Associations?.Del?._Associations?.Rum?.id
            );
          }
        } else {
          emptyShellAvliden();
          //TODO fix
          if (props.onCompleteNyPlacering)
            props.onCompleteNyPlacering(
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              undefined,
              {
                delBen: undefined,
                placeringBen: undefined,
                placeringID: undefined,
                rumBen: undefined,
              },
              undefined
            );
        }
      }
    },
  });
  const emptyShellAvliden = useCallback(() => {
    props.formContext?.update([
      { operation: 'Values', associationName: 'Avlidna' },
      {
        operation: 'Values',
        associationName: 'Person',
        values: {
          _typ: TEDOPOrgPersLokTypEnum.PERSON,
          PERS_NR: persNr.replaceAll('-', ''),
          _Associations: { Adress_Values: {} },
        },
      },
    ]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persNr]);

  useEffect(() => {
    const onlyNumbers = persNr.replaceAll('-', '');
    if (onlyNumbers.length < 12) emptyShellAvliden();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [persNr]);

  const handleChange = (value: string) => {
    value = value.replace(/[^0-9-]/g, '');

    const hyphenIndex = value.indexOf('-');

    let maxBeforeHyphen = 8;
    let maxAfterHyphen = 4;
    let maxLength = 12;

    if (hyphenIndex !== -1) {
      maxLength = 13;
      if (hyphenIndex > maxBeforeHyphen) {
        value =
          value.slice(0, maxBeforeHyphen) +
          '-' +
          value.slice(maxBeforeHyphen).replace(/-/, '');
      }
      if (value.length > maxLength) {
        value = value.slice(0, maxLength);
      }
      if (value.split('-')[1]?.length > maxAfterHyphen) {
        value = value.slice(0, hyphenIndex + maxAfterHyphen + 1);
      }
    } else {
      if (value.length > maxLength) {
        value = value.slice(0, maxLength);
      }
    }

    const isValid = value === '' || /^\d{1,6}(\d{2})?-?\d{0,4}$/.test(value);
    if (!isValid) {
      return;
    }
    setPersNr(value);
  };

  return (
    <EDTextEdit
      onChange={event => handleChange(event)}
      onBlur={() => {
        if (persNr.length === 12 && !persNr.includes('-'))
          setPersNr(persNr.slice(0, 8) + '-' + persNr.slice(8));
      }}
      label="Personnummer"
      value={persNr}
      helperText={
        persNr.replaceAll('-', '').length === 0
          ? 'Värde krävs'
          : persNr.replaceAll('-', '').length < 12
          ? 'Kräver ett fullständigt personnummer'
          : TEDParsePersNr.getInstance().parse(persNr).result !== ParseResult.OK
          ? 'Felaktigt personnummer'
          : ''
      }
      marker={persNr.length > 0}
      disabled={avlidenData?.db?.OPAvliden_All?.[0] !== undefined}
      testId={'Field_Avliden_PersNr'}
      required
      autoFocus
    />
  );
};

export default EDOPAvlidenSearchButtons;
